import React from 'react';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { DAGGraphProps } from './types.js';
import { layoutDAG } from './layout.js';
import { getDeploymentStatusColor } from '../utils/index.js';

/**
 * DAGGraph - Directed Acyclic Graph visualization for BRUTAL deployment pipeline
 *
 * Renders deployment artifacts and their dependencies as a layered DAG.
 * Uses EITELKEIT tokens for all colors (no hard-coded values).
 *
 * @example
 * ```tsx
 * <DAGGraph
 *   nodes={[
 *     { id: 'a', artifact: 'api', version: '1.2.3', status: 'deployed' },
 *     { id: 'b', artifact: 'web', version: '1.2.3', status: 'running' }
 *   ]}
 *   edges={[{ from: 'a', to: 'b' }]}
 *   onNodeClick={(id) => console.log('Clicked:', id)}
 *   width={800}
 *   height={600}
 * />
 * ```
 */
export function DAGGraph({
  nodes,
  edges,
  onNodeClick,
  width,
  height,
}: DAGGraphProps): React.JSX.Element {
  const layoutNodes = layoutDAG(nodes, edges, width, height);

  // Build edge lookup for rendering
  const nodeMap = new Map(layoutNodes.map((n) => [n.id, n]));

  return (
    <svg
      width={width}
      height={height}
      style={{
        backgroundColor: tokens.colors.void,
        fontFamily: tokens.fonts.mono,
        userSelect: 'none',
      }}
    >
      {/* Render edges first (background layer) */}
      {edges.map((edge) => {
        const fromNode = nodeMap.get(edge.from);
        const toNode = nodeMap.get(edge.to);

        if (!fromNode || !toNode) return null;

        return (
          <g key={`${edge.from}-${edge.to}`}>
            {/* Edge line */}
            <line
              x1={fromNode.x}
              y1={fromNode.y}
              x2={toNode.x}
              y2={toNode.y}
              stroke={tokens.colors.border}
              strokeWidth={2}
            />

            {/* Arrow head */}
            <polygon
              points={`${toNode.x},${toNode.y - 25} ${toNode.x - 4},${toNode.y - 20} ${toNode.x + 4},${toNode.y - 20}`}
              fill={tokens.colors.border}
            />
          </g>
        );
      })}

      {/* Render nodes */}
      {layoutNodes.map((node) => {
        const statusColor = getDeploymentStatusColor(node.status);
        const handleClick = () => {
          if (onNodeClick) {
            onNodeClick(node.id);
          }
        };

        return (
          <g
            key={node.id}
            onClick={handleClick}
            style={{
              cursor: onNodeClick ? 'pointer' : 'default',
            }}
          >
            {/* Node circle */}
            <circle
              cx={node.x}
              cy={node.y}
              r={20}
              fill={tokens.colors.surface}
              stroke={statusColor}
              strokeWidth={2}
            />

            {/* Artifact name (above node) */}
            <text
              x={node.x}
              y={node.y - 30}
              textAnchor="middle"
              fill={tokens.colors.text}
              fontSize={tokens.fontSizes.sm}
              fontWeight={tokens.fontWeights.semibold}
            >
              {node.artifact}
            </text>

            {/* Version (below node) */}
            <text
              x={node.x}
              y={node.y + 35}
              textAnchor="middle"
              fill={tokens.colors.textSecondary}
              fontSize={tokens.fontSizes.xs}
            >
              {node.version}
            </text>

            {/* Status badge (to the right of node) */}
            <text
              x={node.x + 30}
              y={node.y + 4}
              fill={statusColor}
              fontSize={tokens.fontSizes.xs}
              fontWeight={tokens.fontWeights.medium}
            >
              {node.status}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
